// The /transport route's search rules, kept apart from the page so the
// route's validateSearch does not pull the whole page (and its booking
// widgets) into every page's first download.
import { isIsoDay } from "./ride-time";
import { parseTileKind, type TileKind } from "./tiles";

/**
 * The hub's search params.
 *
 * - `?route=<slug>` opens the card on that ride (its tile, that route). Every
 *   old route URL 301s here with it (see hub-redirect.ts).
 * - `?ride=<tile>` opens the card on a tile: airport, city, restaurant, night,
 *   agafay, cities or driver.
 * - `?place=<name>&date=YYYY-MM-DD&time=HH:MM`, with `ride=restaurant`, `night`
 *   or `agafay` (the thank-you pages' "Need a ride there?"), fill
 *   the restaurant card in from a table booking.
 */
export interface TransportSearch {
  route?: string;
  ride?: TileKind;
  place?: string;
  date?: string;
  time?: string;
}

/** Keeps each param only when it is well formed; anything else is dropped. */
export function parseTransportSearch(search: Record<string, unknown>): TransportSearch {
  const out: TransportSearch = {};
  const route = typeof search.route === "string" ? search.route.trim().toLowerCase() : "";
  if (/^[a-z0-9][a-z0-9-]{0,120}$/.test(route)) out.route = route;
  const ride = parseTileKind(typeof search.ride === "string" ? search.ride.trim().toLowerCase() : undefined);
  if (ride) out.ride = ride;
  // A restaurant name as the guest booked it: printable text, 3 to 120 characters.
  const place = typeof search.place === "string" ? search.place.replace(/\s+/g, " ").trim() : "";
  if (place.length >= 3 && place.length <= 120 && !/[<>{}]/.test(place)) out.place = place;
  const date = typeof search.date === "string" ? search.date.trim() : "";
  if (isIsoDay(date)) out.date = date;
  const time = typeof search.time === "string" ? search.time.trim() : "";
  if (/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) out.time = time;
  return out;
}
